import {Component, OnInit} from '@angular/core';
import {FormArray, UntypedFormArray, UntypedFormBuilder, UntypedFormGroup} from '@angular/forms';
import {Router} from '@angular/router';

import {TranslateService} from '@ngx-translate/core';

import {finalize, map} from 'rxjs/operators';

import * as moment from 'moment';

import {
  ContractDefaultZoneName,
  ContractStatus,
  ContractStatusDisplay,
  ContractStatusValue,
  CriterionDescriptor,
  PopulationCriteria,
  PopulationCriteriaName,
  PopulationCriterion,
  PopulationFormControls,
  POPULATION_CRITERIA_COOKIE_NAME
} from './create-population.interfaces';

import {BsLocaleService} from 'ngx-bootstrap/datepicker';
import {
  COMMON_OPERATORS,
  DATE_FORMAT,
  LABEL_OPERATORS,
  NUMBER_OPERATORS,
  Operator,
  TREE_OPERATORS
} from '../../../app.constants';
import {
  PopulationFilterTransId,
  PopulationFilterValue
} from '../../../navigation/navbar/context-bar/context-bar.interfaces';
import {CookieService} from '../../cookie-service/cookie.service';
import {EmployeesContextUtilsService} from '../../employees/utils/employee-context-utils.service';
import {FieldType} from '../../form-generator/fields/fields.interfaces';
import {LocalizationService} from '../../localization/localization.service';


@Component({
  selector: 'gp-create-population',
  templateUrl: './create-population.component.html',
  styleUrls: ['./create-population.component.scss']
})
export class CreatePopulationComponent implements OnInit {

  public criteriaDescriptors: CriterionDescriptor[] = [];
  public populationForm: UntypedFormGroup;
  public dateFormat: string;
  public activeRoute: string;
  public loading: boolean = false;
  public populationCriteria: PopulationCriteria;
  public populationCriteriaCookie: PopulationCriteria;
  public acceptedZoneTypes: FieldType[] = [FieldType.DATE, FieldType.ENTITY, FieldType.ENTITY_DATA, FieldType.ENTITY_PREFIXED,
    FieldType.LIST, FieldType.TEXT, FieldType.LABEL];
  public contractStatuses: ContractStatus[] = [
    {value: ContractStatusValue.ACTIVE, display: ContractStatusDisplay.ACTIVE},
    {value: ContractStatusValue.INACTIVE, display: ContractStatusDisplay.INACTIVE},
    {value: ContractStatusValue.FUTURE, display: ContractStatusDisplay.FUTURE}
  ];

  public displayValueForEntities = {};

  get criteria() {
    return this.populationForm.get('criteria') as UntypedFormArray;
  }

  constructor(private employeesContextUtilsService: EmployeesContextUtilsService,
              private formBuilder: UntypedFormBuilder,
              private bsLocaleService: BsLocaleService,
              private cookieService: CookieService,
              private translateService: TranslateService,
              private router: Router,
              private localizationService: LocalizationService) {

    this.populationForm = this.formBuilder.group({
      contractBeginDate: this.formBuilder.group({
        operator: [COMMON_OPERATORS[0].value],
        criterionValue: [''],
        criterionZoneId: ContractDefaultZoneName.CONTRACT_BEGIN_DATE,
        criterionDataIndice: undefined,
        criterionType: FieldType.DATE
      }),
      contractEndDate: this.formBuilder.group({
        operator: [COMMON_OPERATORS[0].value],
        criterionValue: [''],
        criterionZoneId: ContractDefaultZoneName.CONTRACT_END_DATE,
        criterionDataIndice: undefined,
        criterionType: FieldType.DATE
      }),
      contractStatus: this.formBuilder.group({
        operator: [COMMON_OPERATORS[0].value],
        criterionValue: [''],
        criterionZoneId: ContractDefaultZoneName.CONTRACT_STATUS,
        criterionDataIndice: undefined,
        criterionType: FieldType.LIST
      }),
      criteria: this.formBuilder.array([])
    });
  }

  ngOnInit() {
    this.dateFormat = this.localizationService.dateFormat;
    const gpLocale = this.localizationService.currentLanguage === 'en-US' ? 'en' : this.localizationService.currentLanguage;
    this.bsLocaleService.use(gpLocale);
    this.activeRoute = this.router.url;
    if (this.cookieService.getCookie(POPULATION_CRITERIA_COOKIE_NAME)) {
      this.populationCriteriaCookie = JSON.parse(this.cookieService.getCookie(POPULATION_CRITERIA_COOKIE_NAME));
      this.setContractDefaultZonesInitialValues(this.populationCriteriaCookie);
    }
    this.getCriteriaDescriptors();
  }

  public addCriterion(criterionDescriptor: CriterionDescriptor) {
    this.criteria.push(this.createCriterion(criterionDescriptor));
  }

  public createCriterion(criterionDescriptor: CriterionDescriptor): UntypedFormGroup {
    let initialValue;
    let initialOperator = COMMON_OPERATORS[0].value;
    const cookieCriterion: PopulationCriterion = this.populationCriteriaCookie
      ? this.populationCriteriaCookie.criteria
        .find(criterion => (criterion.zoneName ===
          criterionDescriptor.zoneID && criterion.zoneType ===
          criterionDescriptor.type))
      : undefined;
    if (cookieCriterion) {
      initialOperator = cookieCriterion.operator;
      initialValue = criterionDescriptor.type === FieldType.DATE
        ? this.setStringToDate(cookieCriterion.values[0])
        : cookieCriterion.values[0];
    } else {
      initialValue = [''];
    }

    return this.formBuilder.group({
      operator: initialOperator,
      criterionValue: initialValue,
      criterionZoneId: criterionDescriptor.zoneID,
      criterionDataIndice: criterionDescriptor.dataIndice,
      criterionType: criterionDescriptor.type,
      translatedZoneName: criterionDescriptor.transIds.join(' / ')
    });
  }

  public cancelNewPopulation(modal) {
    modal.close();
  }

  public onOperatorChange(index: number, value: string): void {
    const criteriaArray = this.populationForm.get('criteria') as FormArray;
    criteriaArray.at(index).get('operator')?.setValue(value);
  }
  public applyPopulation(modal) {
    if (this.criteriaHasValues()) {
      this.employeesContextUtilsService.updateContextPopulation(
        {value: PopulationFilterValue.CUSTOM, transId: PopulationFilterTransId.CUSTOM}
      );
      this.createPopulationCriteria();
    } else {
      this.employeesContextUtilsService.updateContextPopulation(
        {value: PopulationFilterValue.ALL, transId: PopulationFilterTransId.ALL}
      );
      this.cookieService.deleteCookie(POPULATION_CRITERIA_COOKIE_NAME, '/');
    }
    modal.close();
    this.employeesContextUtilsService.refreshPopulation(this.activeRoute);
  }

  public getCriteriaDescriptors() {
    this.loading = true;
    this.employeesContextUtilsService.getCriteriaDescriptors().pipe(
      finalize(() => this.loading = false),
      map((criteriaDescriptors: CriterionDescriptor[]) => {
        if (criteriaDescriptors.length > 0) {
          // In V1 we accept only 5 types: DATE, ENTITY, LABEL, TEXT, LIST
          this.criteriaDescriptors = criteriaDescriptors.filter(descriptor => this.acceptedZoneTypes.includes(descriptor.type));
          this.criteriaDescriptors.forEach(criterionDescriptor => {
            criterionDescriptor.transIdsTranslations = this.getTransIdsTranslations(criterionDescriptor);
            this.addCriterion(criterionDescriptor);
          });
        }
      })
    ).subscribe();
  }

  public getConditionalOperators(criterionType: string): Operator[] {
    const conditionalOperators = [...COMMON_OPERATORS];
    switch (criterionType) {
      case FieldType.DATE:
        conditionalOperators.push(...NUMBER_OPERATORS);
        break;
      case FieldType.LABEL:
      case FieldType.TEXT:
        conditionalOperators.push(...LABEL_OPERATORS);
        break;
      case FieldType.ENTITY:
        conditionalOperators.push(...TREE_OPERATORS);
        break;
      default:
        break;
    }
    return conditionalOperators;
  }

  public getOperatorDisplayValue(formGroupName: any, criterionType: string, isCriteriaControl: boolean) {
    let selectedOperatorValue;
    let operators;
    if (isCriteriaControl) {
      selectedOperatorValue = this.criteria.value[formGroupName].operator;
      operators = this.getConditionalOperators(criterionType);
    } else {
      selectedOperatorValue = this.populationForm.get(formGroupName).get(PopulationFormControls.OPERATOR).value;
      operators = this.getConditionalOperators(criterionType);
    }
    return operators.find(op => op.value === selectedOperatorValue).display;
  }

  public createPopulationCriteria() {
    this.populationCriteria = {name: PopulationCriteriaName.MY_CUSTOM_POPULATION, criteria: []};
    const filteredCriteria = [
      this.populationForm.get(PopulationFormControls.CONTRACT_BEGIN_DATE).value,
      this.populationForm.get(PopulationFormControls.CONTRACT_END_DATE).value,
      this.populationForm.get(PopulationFormControls.CONTRACT_STATUS).value,
      ...this.populationForm.get(PopulationFormControls.CRITERIA).value
    ].filter(criterion => (criterion.criterionValue !== undefined && criterion.criterionValue !== null && criterion.criterionValue !== ''));

    filteredCriteria.forEach(criterion => {
      const value = criterion.criterionValue;

      const criteriaObject = {
        zoneName: criterion.criterionZoneId,
        translatedZoneName: criterion.translatedZoneName,
        dataIndice: criterion.criterionDataIndice,
        zoneType: criterion.criterionType,
        operator: criterion.operator,
        values: Array.isArray(value) ? value : [value]
      };

      if (criterion.criterionType === FieldType.ENTITY) {
        criteriaObject['displayValue'] = this.displayValueForEntities[criterion.criterionZoneId];
      }

      if (criterion.criterionType === FieldType.LIST) {
        if (criterion.criterionZoneId === '_contractStatus') {
          criteriaObject['displayValue'] = value;
        } else {
          const descriptor = this.criteriaDescriptors
            .filter(desc => desc.type === FieldType.LIST &&
              desc.zoneID === criterion.criterionZoneId)[0];

          criteriaObject['displayValue'] = descriptor?.metadatas?.items.filter(item => item.value === value)[0]?.display;
        }
      }

      if (criterion.criterionType === FieldType.DATE) {
        criteriaObject.values[0] = this.setDateToString(criteriaObject.values[0]);
      }

      this.populationCriteria.criteria.push(criteriaObject);
    });

    this.cookieService.setCookie(POPULATION_CRITERIA_COOKIE_NAME, JSON.stringify(this.populationCriteria), new Date('3000-01-01'), '/');
  }

  public setDateToString(date: Date): string {
    return moment(date).format(DATE_FORMAT);
  }

  public setStringToDate(date: string) {
    return new Date(date);
  }

  public resetPopulationForm() {
    this.populationForm.get(PopulationFormControls.CONTRACT_BEGIN_DATE).get(PopulationFormControls.CRITERION_VALUE).setValue(null);
    this.populationForm.get(PopulationFormControls.CONTRACT_END_DATE).get(PopulationFormControls.CRITERION_VALUE).setValue(null);
    this.populationForm.get(PopulationFormControls.CONTRACT_STATUS).get(PopulationFormControls.CRITERION_VALUE).setValue(null);
    for (let i = 0; i < this.criteria.length; i++) {
      this.criteria.at(i).get(PopulationFormControls.CRITERION_VALUE).setValue(null);
    }
  }

  public setContractDefaultZonesInitialValues(cookie: PopulationCriteria) {
    const contractBeginDateCriterion = cookie.criteria
      .find(criterion => criterion.zoneName === ContractDefaultZoneName.CONTRACT_BEGIN_DATE);
    const contractEndDateCriterion = cookie.criteria
      .find(criterion => criterion.zoneName === ContractDefaultZoneName.CONTRACT_END_DATE);
    const contractStatusCriterion = cookie.criteria
      .find(criterion => criterion.zoneName === ContractDefaultZoneName.CONTRACT_STATUS);

    if (contractBeginDateCriterion) {
      const beginDateValue = this.setStringToDate(contractBeginDateCriterion.values[0]);
      const operator = contractBeginDateCriterion.operator;
      this.populationForm.get(PopulationFormControls.CONTRACT_BEGIN_DATE).get(PopulationFormControls.CRITERION_VALUE)
        .setValue(beginDateValue);
      this.populationForm.get(PopulationFormControls.CONTRACT_BEGIN_DATE).get(PopulationFormControls.OPERATOR)
        .setValue(operator);
    }
    if (contractEndDateCriterion) {
      const endDateValue = this.setStringToDate(contractEndDateCriterion.values[0]);
      const operator = contractEndDateCriterion.operator;
      this.populationForm.get(PopulationFormControls.CONTRACT_END_DATE).get(PopulationFormControls.CRITERION_VALUE)
        .setValue(endDateValue);
      this.populationForm.get(PopulationFormControls.CONTRACT_END_DATE).get(PopulationFormControls.OPERATOR)
        .setValue(operator);
    }
    if (contractStatusCriterion) {
      const contractStatusValue = contractStatusCriterion.values[0];
      const operator = contractStatusCriterion.operator;
      this.populationForm.get(PopulationFormControls.CONTRACT_STATUS).get(PopulationFormControls.CRITERION_VALUE)
        .setValue(contractStatusValue);
      this.populationForm.get(PopulationFormControls.CONTRACT_STATUS).get(PopulationFormControls.OPERATOR)
        .setValue(operator);
    }
  }

  public criteriaHasValues(): boolean {
    return !!this.populationForm.get(PopulationFormControls.CONTRACT_BEGIN_DATE).get(PopulationFormControls.CRITERION_VALUE).value ||
      !!this.populationForm.get(PopulationFormControls.CONTRACT_END_DATE).get(PopulationFormControls.CRITERION_VALUE).value ||
      !!this.populationForm.get(PopulationFormControls.CONTRACT_STATUS).get(PopulationFormControls.CRITERION_VALUE).value ||
      this.criteria.value.some(criterion => !!criterion.criterionValue);
  }

  public onPopulationEntitySelected(item: string, criterion: CriterionDescriptor): void {
    this.displayValueForEntities[criterion.zoneID] = item;
  }

  private getTransIdsTranslations(criterionDescriptor: CriterionDescriptor): string[] {
    const transIdsTranslations: string[] = [];
    for (const transId of criterionDescriptor.transIds) {
      const transIdTranslation = this.translateService.instant(transId);
      if (!transIdsTranslations.includes(transIdTranslation)) {
        transIdsTranslations.push(transIdTranslation);
      }
      if (transIdsTranslations.length === 4) {
        break;
      }
    }
    return transIdsTranslations;
  }

}
